// client/src/hooks/marketing/useMarketingNavBadges.ts
/**
 * Nav badge counts for the Marketing module (P15).
 *
 * useContentReviewCount — posts in 'review' status awaiting human approval.
 * useNewMediaInquiryCount — media inquiries with status 'new' (unread PR inbox items).
 *
 * Both hooks accept an `enabled` flag so ModuleContext can skip the queries
 * when the user is not in the Marketing module.
 */

import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { db, functions } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { COLLECTIONS } from '../../config/collections';

export function useContentReviewCount(enabled = true): number {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId;

  const { data = 0 } = useQuery({
    queryKey: ['marketingPosts', 'navBadge', 'review', organizationId],
    // Counted SERVER-side (#3953): a client count of marketingPosts cannot be proven against its
    // read rule (which excludes briefings) for a non-manager, so it was permission-denied for every
    // non-manager P15 user on every page. Briefings are excluded on the server.
    queryFn: async (): Promise<number> => {
      if (!organizationId) return 0;
      const fn = httpsCallable<{ action: 'getReviewCount'; organizationId: string }, { count: number }>(
        functions,
        'marketing_dispatch',
      );
      const res = await fn({ action: 'getReviewCount', organizationId });
      return res.data.count;
    },
    enabled: enabled && !!organizationId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return data;
}

export function useNewMediaInquiryCount(enabled = true): number {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId;

  const { data = 0 } = useQuery({
    queryKey: ['mediaInquiries', 'navBadge', 'new', organizationId],
    queryFn: async (): Promise<number> => {
      if (!organizationId) return 0;
      const colRef = collection(db, 'organizations', organizationId, COLLECTIONS.MEDIA_INQUIRIES);
      const q = query(colRef, where('status', '==', 'new'));
      const snap = await getCountFromServer(q);
      return snap.data().count;
    },
    enabled: enabled && !!organizationId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return data;
}
